/**
 * Leaflink Global Constants.
 *
 * NOTE: as per convention, all constants are written in UPPERCASE.
 * NOTE: this file is for _global_ constants. If things are specific to a
 *       particular page or only used in one place, they may not belong here.
 */
import { DESTINATION_OPTIONS } from './destinationOptions';
import * as OPTIONS from './options';
import { ROUTE_NAMES } from './routeNames';
import * as STATUSES from './statuses';

export * from './featureFlags';
export * from './seedToSale';
export { DESTINATION_OPTIONS, OPTIONS, ROUTE_NAMES, STATUSES };

export const CSRF_COOKIE_NAME = 'mp-csrftoken';

export const FORM_DATA_HEADER = {
  'Content-Type': 'multipart/form-data',
};

/**
 * LeafLink app names
 */
export const APPS = {
  MARKETPLACE: 'marketplace',
  PAYMENTS: 'payments',
} as const;

/**
 * Application layouts
 */
export const LAYOUTS = {
  DEFAULT: 'default',
  FULL: 'full',
};

/**
 * Leaflink static URLs
 * Please refer to `leaflink/apps/apis/v2/urls.py` for reference
 */
export const URLS = {
  /**
   * Intended for use only in "test" environments.
   */
  ORIGIN_TEST: window.location.origin, // defaults to http://localhost:3000
  API: '/api/v2/',
  INTERNAL: '/api/internal/',
  APP_CONFIG: 'application/config/',
  CUSTOM_ERROR: 'error_format=jsonapi',
  BULK: 'bulk=true',
  SPLIT: 'splitio/treatments',
  ACCOUNTING: {
    ACCOUNTS: 'accounting/accounts',
    CUSTOMERS: 'accounting/customers',
    PREFERENCES: 'accounting/qb-preferences',
    PRODUCTS: 'accounting/products',
    PUSH_INVOICE: 'accounting/push-invoice',
  },
  ADMIN: {
    COMPANIES: 'admin/companies',
    COMPANY_STAFFS: 'admin/company-staffs',
    ORDER_CO: {
      BASE: 'admin/order-co',
      ORGANIZATIONS: 'organizations',
    },
    PRODUCT_CSV_INGESTION: '/api/ll/ingestion',
    PRODUCT_CSV_INGESTION_ITEM: '/api/ll/ingestion/:id/ingestion-item',
    LICENSE_TYPES: 'admin/license-types',
    METRC_PACKAGE_INSPECTOR: 'traceability/admin/metrc-packages/',
  },
  BACKFILL: {
    WEBHOOK_BACKFILL: 'webhook-backfill',
  },
  BRAND_OWNER: {
    BASE: 'brand-owner',
    BRANDS: 'brands',
    ADD_BRAND: 'add-new',
    CUSTOMERS: 'customers',
    DISTRIBUTORS: 'distributors',
    LINE_ITEMS: 'line-items',
    ORDER_TAXES: 'order-taxes',
    ORDERS: 'orders',
    PRODUCTS: 'products',
    PRODUCT_LINES: 'product-lines',
  },
  CART: {
    VALIDATE_PROMOCODE: 'promocodes/validate',
    INTERNAL_APPROVALS_STATS: 'internal-approvals/orders/stats',
  },
  CARTS: {
    CHECKOUT: 'purchases/checkout',
    CLEAR: 'purchases/carts/clear',
    DEFAULT: 'purchases/carts',
    GUEST_CARTS: 'purchases/guest_carts',
    GUEST_CARTS_CLEAR: 'purchases/guest_carts/clear',
    GUEST_CARTS_ITEMS: 'purchases/guest_carts/items',
    GUEST_CHECKOUT: 'purchases/guest_checkout',
    ITEMS: 'purchases/carts/items',
    STATS: 'purchases/carts/stats',
  },
  COMPANY: {
    ACTIVE_CAMPAIGN: 'campaigns/active',
    APPLICATIONS: 'applications',
    AVAILABLE_PRODUCTS: 'available-products',
    BRANDS: 'brands',
    CATEGORY_MANAGEMENT_WAITLIST: 'category-management-waitlist',
    COMPANY_SETTINGS: 'company-settings',
    CREDITS: 'credits',
    CREDIT_DOWNLOADS: 'credit-downloads',
    INITIATE_BANK_LINK: 'initiate-bank-linking',
    LICENSES: 'licenses',
    LIST: 'companies',
    ORDERS_STATS: 'orders-stats',
    REGENERATE_TOKEN: 'regenerate_token',
    SELLERS: 'sellers',
    STAFF: 'company-staff',
    SHARED_SELL_THROUGH_DATA: 'shared-sell-through-data',
    SELLERS_EXCLUDED_FROM_POST: 'sellers/excluded_from_post/',
    STAFF_WITH_PERMISSIONS: 'company-staff-permissions',
    STAFF_WITH_PERMISSIONS_EDIT: 'company-staff-permissions/:id/',
    SWITCH: 'companies/switch/',
    TRUSTED_SELLER: 'customer-stats',
    PAYMENT_PROGRAM_CHANGE_REQUESTS: 'payment-program-change-requests',
    STORE: 'store',
    STORE_EDIT: 'store/:id',
    STORE_REQUESTS: 'store-requests',
    POS_INTEGRATION: 'point-of-sale-integration/',
  },
  COMPLIANCE: {
    LICENSE_TYPES: 'compliance/license-types/',
  },
  CONNECTIONS: {
    INSTALL: 'connect/install/:connectionType/',
    COMPLETE: 'connect/complete/:connectionType/',
    LIST: 'connections/',
    RESOURCE_MAPPINGS: 'resource-mappings/',
    RESOURCE_MAPPINGS_SEARCH: 'resource-mappings/search/',
    QUICKBOOKS_CUSTOMERS: 'resources/quickbooks/customers/',
    QUICKBOOKS_ITEMS: 'resources/quickbooks/items/',
    QUICKBOOKS_SETTINGS: 'connect/settings/marketplace-quickbooks/',
    USER_CREDENTIALS_BIOTRACK: '/rpc/trace-biotrack/user-credentials',
  },
  CUSTOMER: {
    BULK: 'crm/customers-bulk',
    CONTACTS: 'crm/contacts',
    CUSTOMER_CONTACTS: 'crm/customer-contacts',
    CUSTOMER_CONTACT_DETAIL: 'customer-contacts',
    CUSTOMER_REQUESTS: 'crm/customer-requests',
    DELIVERY_ZONE: 'zones/:zoneId/',
    DELIVERY_ZONE_ZIP_CODES: 'zones/zip-codes/',
    DELIVERY_ZONES: 'zones/',
    ELIGIBLE_CUSTOMERS: 'crm/customer-can-buy-product',
    GROUPS: 'customer-groups',
    IMPORT: 'crm/import-customers',
    LICENSE_TYPES: 'crm/license-types',
    LIST: 'crm/customers',
    ORDER_PAYMENT_OPTIONS: 'crm/customer-payment-options',
    PAYMENT_TERMS: 'commerce/payment-terms',
    PAYMENT_OPTIONS: 'payment-options',
    PAYMENTS_INVITE: 'crm/customers/:customerId/invite-to-payments/',
    CUSTOMER_PAYMENT_OPTIONS: 'customer-payment-options',
    POTENTIAL_CUSTOMERS: 'potential-customers',
    STATS: 'crm/customer-stats',
    SERVICE_ZONES: 'crm/service-zones',
    STATUSES: 'crm/customer-statuses',
    TAGS: 'crm/customer-tags',
    TASKS: 'crm/customer-tasks',
    USERS: 'crm/users',
    TIERS: 'customer-tiers',
    WAREHOUSE_DETAIL: 'customer/:customerId/fulfillment-warehouse',
  },
  DOMAIN: {
    COMPANIES: 'domain/companies',
    COMPANY: 'domain/companies/:id',
  },
  FEATURE_ENROLLMENT: {
    BASE: 'feature-enrollment',
    DRAFT_ORDERS: 'feature-enrollment/draft-orders',
    MODIFY_PLAN: 'feature-enrollment/modify-plan',
    PLAN_SUBSCRIPTIONS: 'feature-enrollment/plan-subscriptions',
    PRODUCTS: 'feature-enrollment/products',
  },
  HIGHLIGHT_WIDGETS: 'highlight-widgets',
  INSIGHTS: {
    BASE: 'insights',
  },
  INTEGRATIONS: {
    BASE: 'integrations',
    SETTINGS: 'integration-settings',
    SIGMA: 'sigma/signed-report-url/',
    SYSTEMS: 'integrated-systems',
  },
  INVENTORY: {
    ACTIVE_PRODUCT: 'active-products/:productId/',
    ACTIVE_PRODUCTS: 'active-products',
    BATCHES: 'batches',
    BATCH_DOCUMENTS: 'batch-documents',
    BULK_UPDATE: 'bulk_update',
    CUSTOM_MENU_ITEMS: 'price-schedule-items',
    CUSTOM_MENU_LIST: 'price-schedules',
    DEFAULT: 'product',
    ELIGIBLE_COMPANIES: 'eligible-companies',
    FACILITIES: 'inventory/facilities',
    IMAGES: 'product-images',
    INGESTION: '/ingestion',
    /** @type {'product-inventory'} */
    INVENTORY_MANAGEMENT: 'product-inventory',
    ITEM: 'inventory/items/:itemId',
    ITEMS: 'inventory/items',
    /** @type {'products'} */
    LIST: 'products',
    LISTING_STATES: 'listing-states',
    PRODUCT: 'products/:productId',
    PRODUCT_BATCHES: 'product-batches/:productId',
    PRODUCT_LINES: 'product-lines',
    PROMO_CODES: 'promocodes',
    WMS_PRODUCT_INVENTORY: 'wms-product-inventory',
    STRAIN_CLASSIFICATIONS: 'strain-classifications',
    STRAINS: 'strains',
    COMPANY_STRAINS: 'company-strains',
  },
  INVENTORY_INSIGHTS: {
    LEADERBOARD: 'inventory-insights/leaderboard/',
    METADATA: 'inventory-insights/metadata/',
    CUSTOMER_FEEDBACK: 'customer-feedback/',
    NEGOTIATE: 'inventory-insights/negotiate/',
    PAR: 'inventory-insights/par/',
  },
  LISTINGS: {
    PRODUCTS: 'listings/products',
    PRODUCTS_ASSIGNED: 'listings/product-assigned-to-buyer',
  },
  LOGISTICS: {
    BASE: 'logistics',
    BRINGG: {
      GET_PICKUP_STOPS: 'bringg/get-pickup-stops/',
      PUSH_ORDERS: 'bringg/push_inventory_to_pickup_tasks/',
      PUSH_DELIVERY_STOPS: 'bringg/push-delivery-stops/',
      PUSH_PICKUP_STOPS: 'bringg/push_pickup_stops/',
    },
    BUYERS: 'logistics-buyers',
    COMPANIES: 'logistics-companies',
    DELIVERIES_CSV_TEMPLATE:
      'https://media-leaflink-dev.s3.us-west-2.amazonaws.com/media/images/csv/bulk_delivery_request_template.csv',
    DELIVERY_PREFERENCES: 'delivery-preferences',
    DELIVERIES_ORDERS: 'deliveries-orders',
    TRANSPORT_ORDERS: 'logistics/orders',
    INBOUND_ORDER_MAPPPINGS: {
      ASNS: 'fulfillment/admin/asns',
      TRANSFERS: 'fulfillment/admin/transfers',
      LINK_TRANSFER_ASN: 'fulfillment/admin/link_transfer_asns/',
      ITEMS: '',
    },
    L3_ORDER_ROUTE_DETAILS: 'get-route-details',
    L3_ORDER_ROUTE_DIRECTIONS: 'get-directions',
    METRC_KEYS: 'metrc/keys',
    LOGISTICS_ORDERS: 'logistics-orders',
    REPROCESS: 'reprocess',
    SHIPMENTS: 'logistics/shipments',
    TMS: {
      TRANSPORT_SCHEDULES: 'transport-schedules/by-companies',
      TRANSPORT_PICKUP_SCHEDULES_ZIPCODES: 'transport-schedules/by-zipcodes/pickup/available-dates',
      TRANSPORT_DELIVERY_SCHEDULES_ZIPCODES: 'transport-schedules/by-zipcodes/delivery/available-dates',
      TRANSPORT_SETTINGS: {
        STATE_MARKETS: 'transport-settings/state-markets',
        STATES: 'transport-settings/states',
        TIMEZONES: 'transport-settings/timezones',
      },
    },
    TRANSFER: 'transfer',
    TRANSPORT_ONLY_ORDER: 'logistics/transport-orders/:orderId/',
    TRANSPORT_ORDER: 'logistics/orders/:orderId/',
    ORDERS: 'orders',
    UPDATE_TRANSPORT_ORDER: 'logistics/transport-orders/:orderId/',
    UPLOADED_PURCHASE_ORDERS: 'purchase-orders',
    UPLOAD_OFFLINE_ORDERS: 'purchase-orders/upload/',
    UNLINKED_CUSTOMERS: 'logistics-unlinked-customers',
    UPLOAD: 'logistics/orders/upload/',
    TRANSPORT_DATES: 'transport-dates/buyer-delivery-dates',
    TRANSPORT_ORDER_DESTINATIONS: 'transport-order-destinations',
    LOGISTICS_TRANSPORT_ORDERS: 'logistics/transport-orders',
  },
  ONBOARDING: {
    COMPANY_BILLING: 'billing/company-billing/',
    COMPANY_BILLING_PATCH: 'billing/company-billing/:companyId/',
    POS_INTEGRATION_VALIDATE: 'point-of-sale-integration/validate/',
  },
  ORDERS: {
    BASE: 'commerce/orders',
    BUYER_SUMMARY: 'commerce/orders-sent/:uuid/',
    BUYER_SUMMARY_INFO: 'commerce/orders-sent/:uuid/order-information/',
    BUYER_SUMMARY_INFO_UPDATE: 'commerce/orders-sent/:uuid/order-information-update/',
    CALCULATE_LINE_ITEMS: 'commerce/orders/:orderId/calculate-line-items/',
    INTERNAL_APPROVALS: 'internal-approvals/orders/',
    INTERNAL_APPROVAL_DETAILS: 'internal-approvals/orders/:internalApprovalId/',
    INTERNAL_APPROVAL_DETAILS_QUOTE: 'internal-approvals/orders/:internalApprovalId/quote/',
    COLLABORATIVE_RECEIVED: 'commerce/orders-sent/collaborative',
    COLLABORATIVE_SENT: 'commerce/orders/collaborative',
    COMBINE: 'combine',
    CLONE: 'clone',
    CSV_ORDER_CREATE: 'orders/csv-import/',
    ELIGIBLE_PRODUCTS: 'internal-approvals/orders/:internalApprovalId/eligible-products',
    LINE_ITEMS: 'line-items',
    LOGS: 'audit/order',
    MANAGE_PENDING_INTERNAL_APPROVAL: ':companySlug/commerce/orders/poms/:orderNumber',
    MARK_DELIVERED: 'mark-delivered',
    OVERVIEW: 'orders/overview',
    PACKAGES: 'attached-packages',
    PAYMENT_TYPES: 'delivery-provider-payment-types',
    PAYMENTS: 'order-payments',
    RECEIPT_LOG: 'commerce/:slug/orders/:uuid/receipt-log/',
    RECEIVED: 'orders-received', // `orders/received`
    REMOVE_PROMO_CODE: 'cart/order/promocode',
    RETURNS: 'returns',
    REORDER: 'reorder',
    SENT: 'commerce/orders-sent',
    SHIPPING: 'shipping/orders',
    SHIPPING_MANIFESTS: 'shipping-manifests',
    SUMMARY: 'summary',
    SELLER_SUMMARY: 'commerce/orders/:uuid/summary/',
    SELLER_SUMMARY_COMMENTS: 'commerce/orders/:uuid/comments/',
    SELLER_SUMMARY_DOCUMENT: 'commerce/orders/:uuid/documents/:documentId/',
    SELLER_SUMMARY_DOCUMENTS: 'commerce/orders/:uuid/documents/',
    SELLER_SUMMARY_INFO: 'commerce/orders/:uuid/order-information/',
    SELLER_SUMMARY_INFO_UPDATE: 'commerce/orders/:uuid/order-information-update/',
    SELL_THROUGH_HISTORY: 'commerce/:slug/orders/:uuid/sell-through',
    SELL_THROUGH_HISTORY_PRODUCT: 'commerce/:slug/orders/:uuid/sell-through/:productId',
    SELL_THROUGH_HISTORY_ADJUSTMENTS: 'commerce/:slug/orders/:uuid/adjustments/:productId',
    TAXES: 'commerce/order-taxes', // `orders/taxes`
    TRANSITION: 'transition',
  },
  PRICING: {
    VOLUME_DISCOUNTS: 'pricing/volume-discounts/',
    VOLUME_DISCOUNT: 'pricing/volume-discounts/:id/',
    VOLUME_DISCOUNT_UPDATE_STATUS: 'pricing/volume-discounts/:id/update-status/',
    VOLUME_DISCOUNTS_FOR_BRANDS: 'pricing/volume-discounts/brands/',
    VOLUME_DISCOUNTS_FOR_ORDER: 'pricing/volume-discounts/orders/:uuid/',
  },
  REGISTRATION: {
    REGISTER_RETAILER: 'register/retailer/',
    RETAILER_LICENSE_FILE: 'register/retailer/license-document',
    RETAILER_LICENSE_OPTIONS: '/register/retailer/options/',
    RETAILER_SECONDARY_FILE: 'register/retailer/secondary-proof-file',
  },
  REPLENISHMENT: {
    BASE: 'replenishment',
    CATALOG: 'replenishment/catalog',
    CATALOG_COMPANIES: 'replenishment/catalog-companies',
    CATALOG_FILE_UPLOAD: 'replenishment/catalog-file-upload',
    CATALOG_PRODUCT: 'replenishment/catalog/:productId',
    DESTINATIONS: 'replenishment/destinations',
    ORDERS: 'replenishment/orders',
    INBOUND_ORDERS: 'replenishment/orders-inbound',
    INBOUND_ORDERS_FILTERS: 'replenishment/orders-inbound-filters',
    INBOUND_SHIPMENT_SUMMARY: 'replenishment/orders-inbound/:uuid',
    INVENTORY: 'replenishment/inventory',
    RETAILERS: 'replenishment/retailers',
    SHIPMENTS: 'replenishment/shipments',
    WAREHOUSES: 'replenishment/warehouses',
  },
  REPORT: {
    CUSTOMER_STATS: 'customer-stats',
    DOWNLOAD: 'reports',
    SCHEDULE: 'report-schedules',
    TYPES: 'report-download-types',
  },
  S2S: {
    API: '/s2s-api/v2/', //TODO: move to same base URL
    ATTACHED_PACKAGES: 'attached-packages',
    PACKAGE_ORDERS: '/attached-packages/%label%/orders/',
  },
  SELL_THROUGH: {
    ENROLLMENT: 'sell-through/enrollment/',
    ACKNOWLEDGE: 'sell-through/enrollment/acknowledge/',
    REJECT: 'sell-through/enrollment/opt-out/',
    ACCEPT_TERMS: 'sell-through/enrollment/accept-terms/',
    REMOVE_COMPLETION_MESSAGE: 'sell-through/enrollment/remove-completion-message/',
    OPT_IN: 'sell-through/enrollment/opt-in/',
  },
  SETTINGS: {
    AVAILABLE_PERMISSIONS: 'companies/available_permissions',
    BASE: 'settings',
    BILLING: {
      BASE: 'billing',
      AUTHORIZE: {
        ACCEPT_CUSTOMER: 'authorize/get-accept-customer-profile-page',
        ACCEPT_HOST: 'authorize/get-accept-payment-page',
        PAYMENT_PROFILES: 'authorize/get-payment-profiles',
      },
      BANK_ACCOUNTS: 'bank-accounts',
      INVOICES: 'invoices',
      MAKE_PENDING: 'make-pending',
      PAY: 'pay',
      PAYMENT_METHODS: 'payment-methods',
      PLAID_LINK_TOKEN: 'link-token',
      PLAID_VERIFY: 'plaid-verify',
    },
    BILLING_DUE: 'billing-due',
    BRAND: 'brands/:id/',
    BRANDS: 'brands/',
    INVITE_BRAND: 'invite-brand',
    INVITE_BRAND_ACCEPT: 'invite-brand/:inviteId/accept',
    INVITE_BRAND_REJECT: 'invite-brand/:inviteId/reject',
    COMPANY_PAYMENT_OPTIONS: 'company-payment-options',
    TIMEZONES: 'compliance/settings-timezones',
    LICENSES: {
      BASE: 'licenses',
      COMPANY_LICENSES: 'compliance/settings-company-licenses',
      LICENSE_CHANGE_REQUESTS: 'license-change-requests',
      LICENSE_TYPES: 'compliance/settings-license-types',
      TOGGLE_ARCHIVE: 'toggle-archive',
    },
    PAYMENT_METHODS: 'payment-methods',
    PAYMENT_TERMS: 'payment-terms',
    SELLER_COMPANY_PAYMENT_OPTIONS: 'company-payment-options/seller',
    TAXES: {
      TAX_SCHEDULES: 'taxes/tax_schedules',
    },
  },
  SHIP_ASSIST: {
    BASE: 'ship-assist',
    PACKING_ORDERS: 'packing-orders',
    TOTES: 'totes',
  },
  SHOP: {
    BRAND: {
      BASE: 'brands',
      EMAIL_MENU: 'email-menu',
      FEATURED_PRODUCT: 'products/featured',
      LICENSE_TYPES: 'license-types',
      MENU_EXPORT: 'menu-downloads',
      PREVIEW: {
        BASE: 'preview',
        PRODUCTS: 'products/preview',
        FEATURED_PRODUCT: 'products/featured/preview',
        PRODUCT_MENU_POSITION: 'product-menu-position',
        PRODUCT_LINE_MENU_POSITION: 'product-line-menu-position',
      },
      PRODUCTS: {
        BASE: 'products',
        BUYERS: 'buyers',
      },
      COMPANIES_ALLOWED_TO_PURCHASE_BRAND: 'brand/:brandSlug/companies/',
      BRAND_BY_SLUG: 'brand/:brandSlug/detail/',
    },
    BRANDS: 'brands/shop-brands',
    DEALS: 'shop/deals-old',
    DISCOVER: 'shop/discover',
    EXPRESS_MENU: {
      BASIC_CUSTOMERS: 'customers-basic',
      BRAND: 'express-menu/:menuSlug/brand',
      EMAIL_MENU: 'express-menu/:menuSlug/email-menu/',
      PAYMENT_OPTIONS: 'express-menu/payment-options',
      PRODUCTS: 'express-menu/:menuSlug/products',
      USER_COMPANIES: 'user_companies',
    },
    ACTIVITY_FEED: 'purchasable-product-activities',
    LEARN_MORE: 'learn-more-requests',
    NOTIFY_ME: 'notify-me-requests',
    PRODUCT_FILTERS: 'shop/filters',
    PRODUCTS: 'shop/products',
    PUBLIC_MENU: {
      BRAND: 'public-menu/brand',
      FEATURED_PRODUCT: 'public-menu/featured',
      PRODUCTS: 'public-menu/products',
    },
    BUYER_PROMOCODES: 'buyer-promocodes',
    PURCHASE_LIST: {
      ADD_TO_CART: 'add-to-cart',
      BASE: 'purchase-list',
      HISTORY: 'shop/purchase-history',
      ITEM: 'item',
    },
    FAVORITES: {
      BASE: 'favorites',
    },
    VENDOR: {
      BASE: 'brands/vendor',
      PRODUCTS: 'products',
    },
    SPONSORSHIPS: 'sponsorships',
    SAMPLE_REQUESTS: 'sample-requests',
    SEND_MESSAGE: 'discussion',
  },
  STATES: {
    LIST: 'locale/states/',
  },
  SYSTEM_NOTIFICATIONS: {
    LIST: 'toasts/',
  },
  TRACEABILITY: {
    ACTIONS: {
      REFRESH_PRODUCTS_PACKAGES: 'traceability/actions/refresh-products-packages',
      REFRESH_RESOURCES: 'traceability/actions/refresh-resources',
    },
    ATTACH_PACKAGES_TEMPLATE: 'traceability/templates/:templateId/packages',
    AVAILABLE_TAGS: 'traceability/available-tags',
    DELETE_PACKAGE_TEMPLATE: 'traceability/templates/:templateId/packages/:packageId',
    ITEMS: 'traceability/items',
    NEW_PRODUCT: 'traceability/items/:itemId/products',
    LAB_TEST_RESULTS_PDF: 'traceability/lab-test-results-document/:packageId',
    LOCATIONS: 'traceability/locations',
    SETTINGS: 'traceability/settings',
    PACKAGES: 'traceability/packages',
    PRODUCT_PACKAGE_FILTERS: 'traceability/product-package-filters',
    PRODUCTS: 'traceability/products',
    PRODUCTS_STATISTICS: 'traceability/statistics/products',
    REFRESH_REQUESTS: 'traceability/refresh-requests',
    STATISTICS: 'traceability/statistics/:moduleName',
    SUGGESTED_MANIFEST: 'traceability/transfers/suggested',
    SUGGESTED_MANIFEST_MATCHES: 'traceability/transfers/bulk-suggested',
    TEMPLATES: 'traceability/templates',
    TEMPLATES_EDIT: 'traceability/templates/:templateId',
    TRANSFER_TYPES: 'traceability/transfer-types',
    TRANSFERS: 'traceability/transfers',
    STAFF: 'traceability/staff',
    STRAINS: 'traceability/strains',
    ORDER_MANIFESTS: 'traceability/orders/:orderNumber/transfers',
    ATTACH_ORDERS_TO_MANIFEST: 'traceability/transfers/:transferId/orders',
  },
  USERS: {
    ME: 'users/me',
    SEARCH: 'users/search',
    SET_LANGUAGE: 'users/set_language',
  },
  WAREHOUSE: {
    ASNS: 'asns',
    BASE: 'warehouse',
    BRANDS: 'brands',
    DIRECTIONS: 'directions',
    ELIGIBLE_TRANSPORT_COMPANIES: 'eligible_transport_companies',
    FULFILLMENT_QUEUE: 'fulfillment-queue-orders',
    OUTBOUND_QUEUE: 'outbound-queue-orders',
    ROUTE: 'route',
    SELLERS: 'sellers',
    SHIPMENTS: 'warehouse/shipments',
  },
} as const;

export const UNIFIED_VERSION = {
  v1: '2022-10-31',
};

/**
 * Leaflink marketing site static URLs
 */
export const MARKETING_URLS = {
  DIGITAL_ONBOARDING_REGISTER: '/payments/sign-up',
  DISCLAIMER: 'https://www.leaflink.com/disclaimer-terms',
  FACEBOOK: 'https://www.facebook.com/LeafLinkUS',
  HOME: 'https://www.leaflink.com/',
  INSTAGRAM: 'https://www.instagram.com/leaflink_/',
  LINKEDIN: 'https://www.linkedin.com/company/leaflink/',
  LOGIN: '/accounts/login/',
  PAYMENTS_REGISTER: '/product/payments/payments-form',
  PRIVACY: 'https://www.leaflink.com/legal/privacy-policy',
  RETAILER_REGISTRATION: '/register/retailer/',
  REQUEST_DEMO: '/brands/request-demo',
  TERMS: 'https://www.leaflink.com/legal/leaflink-terms-of-service/',
  TWITTER: 'https://twitter.com/LeafLinkUS',
};

export const CUSTOMER_SUPPORT_URL = 'https://help.leaflink.com/hc/en-us/requests/new/';

/**
 * OrderReturn transition urls
 */
export const ORDER_RETURN_TRANSITIONS = Object.freeze({
  [STATUSES.RETURN.COMPLETE.id]: 'complete',
  [STATUSES.RETURN.REJECTED.id]: 'reject',
});

/**
 * OrderReturn reverse transition urls
 */
export const ORDER_RETURN_REVERSE_TRANSITIONS = Object.freeze({
  [STATUSES.RETURN.COMPLETE.id]: 'reverse_complete',
  [STATUSES.RETURN.REJECTED.id]: 'reverse_reject',
});

/**
 * CSS Variables
 */
export const CSS_VARS = {
  GRID_GUTTER: 'grid-gutter',
  TOP_HEADER_HEIGHT: 'top-header-height',
};

/**
 * Key Codes
 */
export const KEY_CODES = {
  DOWN: 40,
  ENTER: 13,
  ESCAPE: 27,
  LEFT: 37,
  SPACE: 32,
  RIGHT: 39,
  UP: 38,
};

/**
 * North America State Codes
 */
export const STATE_CODES = {
  CA: 5, // California
};

/**
 * Date Formats
 */
export const DATE_FORMATS = {
  DEFAULT: 'YYYY-MM-DD hh:mm A', // TODO find a better name
  YYYY_MM_DD: 'YYYY-MM-DD',
  YYYY_MM_DD_DATE_FNS: 'yyyy-MM-dd',
  MM_DD_YYYY: 'MM/DD/YYYY',
  WEEK_DAY: 'iiii',
  WEEK_DAY_SHORT: 'E',
  DEFAULT_TIME: 'hh:mm A',
  DEFAULT_TIME_SHORTER: 'h:mm A',
  DEFAULT_WITH_TIMEZONE: 'YYYY-MM-DD hh:mm A zz',
  TWELVE_HOUR_TIME: 'hh:mm',
  DEFAULT_TIME_WITH_TIMEZONE: 'hh:mm A zz',
  TIMESTAMP_IN_SECONDS: 't',
  MONTH_LONG: 'MMMM dd, yyyy',
  MONTH_SHORT: 'MMM dd, yyyy',

  // DEPRECATED. Do not use:
  /** @deprecated */
  MONTH_DAY_YEAR_AT_TIME: 'MMM DD, YYYY @ h:mmA',
  /** @deprecated */
  YYYY_MM_DD_SLASHES: 'YYYY/MM/DD',
  /** @deprecated */
  YYYY_MM_DD_SLASHES_DATE_FNS: 'yyyy/MM/dd',
  /** @deprecated */
  ISO8601: "YYYY-MM-DD'T'HH:mm:ssXXX",
} as const;

/**
 * Times used to convert a date. ie. Set a date to Noon
 * DEPRECATED. Do not use:
 */
export const TIMES = {
  NOON: {
    hours: 12,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  },
};

/**
 * Regular Expression used to validate 24h-formatted time
 */
export const TWENTY_FOUR_HOUR_REGEX = /^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;

/**
 * Regular Expression used to validate 12h-formatted time
 */
export const TWELVE_HOUR_REGEX = /^(0?[1-9]|1[012])(:[0-5]\d)$/;

/**
 * Debounce used for firing side-effects (tracking, etc)
 */
export const DEBOUNCE = Object.freeze({
  FAST: 200,
  MEDIUM: 500,
  SLOW: 3000,
});

/**
 * Timeout for UI/UX effects
 */
export const UI_TIMEOUT = 3000;

/**
 * Timeout for notify toastrs
 */
export const NOTIFY_TIMEOUT = 6000;

/**
 * Path to generic LL placeholder image
 */
export const PLACEHOLDER_IMAGE_URL = '/static/img/placeholder-image.jpg';

/**
 * Authorize.net
 * Accept Hosted & Customer Form URL
 */
export const AUTHORIZE_NET_URL = {
  EDIT_PAYMENT: 'authorize.net/customer/manage',
  MAKE_PAYMENT: 'authorize.net/payment/payment',
};

/**
 * Tableau API url
 * Note: Tableau allows specifying only the major version (ex: tableau-2.min.js)
 * which fetches the latest version; however, doing so uses document.write
 * internally, which prevents the API script from loading due to the following:
 * https://developers.google.com/web/updates/2016/08/removing-document-write
 * @see https://help.tableau.com/current/api/js_api/en-us/JavaScriptAPI/js_api_concepts_get_API.htm
 */
export const TABLEAU_API_URL = 'https://public.tableau.com/javascripts/api/tableau-2.8.1.min.js';

/**
 * Chili Piper url
 */
export const CHILI_PIPER_URL = 'https://js.chilipiper.com/marketing.js';

/**
 * License colors
 */
export const LICENSE_COLORS = Object.freeze({
  ADU: 'tw-bg-blue-500',
  REC: 'tw-bg-blue-500',
  MED: 'tw-bg-yellow-500',
  COM: 'tw-bg-royal-500',
  ANC: 'tw-bg-orange-500',
  CAN: 'tw-bg-green-500',
});

/**
 * Mount point for `ll-dropdown`, et al.
 */
export const MOUNT_POINT_CLASS = '.mount-point-container';

export const SPONSORSHIP_POSITIONS = Object.freeze({
  DEALS: 'deals',
  DISCOVERY: 'discovery',
  ORDER_SUBMIT: 'order-submit',
  SHOP_BRANDS: 'shop-brands',
  SHOP_BRANDS_FEATURED_BRAND: 'shop-brands-featured-brand',
  SHOP_BRANDS_SIDEBAR: 'shop-brands-sidebar',
  SHOP_PRODUCTS: 'shop-products',
});

/**
 * Sold by options.
 * Used on MOv3 and BME order create and edit.
 */
export const SOLD_BY = {
  UNITS: 1,
  CASES: 2,
};

export const ROUND_PRECISION = {
  CURRENCY: 2,
  INVENTORY: 4,
};

export const CLOUDINARY_URL = 'https://res.cloudinary.com/leaflink/image/fetch/';

// marking properties as readonly so we can create
// a string union type of the values
export const LLLV_CHANGE_TRIGGERS = Object.freeze({
  APPLY: 'apply',
  FILTER: 'filter',
  LOAD: 'load',
  PAGE: 'page',
  SEARCH: 'search',
  SORT: 'sort',
});

export const TIME_ZONES = {
  US: {
    CENTRAL: 'America/Chicago',
    EAST: 'America/New_York',
    MOUNTAIN: 'America/Denver',
    WEST: 'America/Los_Angeles',
  },
};

export const REJECTION_REASON = {
  ACCOUNT: 'account',
  DELIVERY: 'delivery',
  ORDER: 'order',
  PAYMENT: 'payment',
  PRODUCT: 'product',
  OTHER: 'other',
};

export const REPORT_TYPES = {
  ACCOUNTS_RECEIVABLE: 'accounts-receivable',
  BRAND_OWNER_BRAND_PERFORMANCE: 'brand-owner-brand-performance',
  BRAND_OWNER_ORDERS_REPORT: 'brand-owner-orders-report',
  ORDERED_PRODUCTS_BUYER: 'ordered-products-buyer',
  ORDERS_RECEIVED: 'orders-received',
  ORDERS_SENT: 'orders-sent',
};

/**
 * Available tableau report paths.
 */
export const TABLEAU_REPORTS = {
  RETAILER: 'tableau-retailer',
  ADVERTISING: 'tableau-ads',
  REPLENISHMENT: 'tableau-replenishment',
};

export const REGEX = {
  NON_ALPHANUMERICAL: /[^a-zA-Z\d]/,
};

/**
 * Highlight Widget
 */
const highlightWidgetUrlParams = 'utm_source=ll-product&utm_medium=widget';

export const HIGHLIGHT_WIDGET_CONFIGS = {
  PAYMENTS_CTA: {
    callToAction: 'common.learnMore',
    description: 'shop.brands.paymentsCta.description',
    digitalOnboardingUrl: `${MARKETING_URLS.DIGITAL_ONBOARDING_REGISTER}`,
    id: '4',
    image: '/static/img/payments-cta.png',
    imageAltText: 'LeafLink Payments',
    title: 'shop.brands.paymentsCta.title',
    url: `${MARKETING_URLS.PAYMENTS_REGISTER}?${highlightWidgetUrlParams}`,
  },
};

/**
 * Buyer Sources
 */
export const BUYER_SOURCES = Object.freeze({
  BUYER_VERIFICATION: 'buyer-verification',
});

export const CONFIRM_STOPS_TYPES = Object.freeze({
  DELIVERY: 'delivery',
  PICK_UP: 'pickup',
});

export const PAYMENTS_TYPES = {
  CUSTOM: 'custom', // This is necessary for the Seller-side Order Summary
  NONE: 'none', // This is necessary for the Seller-side Order Summary
  DIRECT: 'direct',
  FLEXIBLE: 'flexible',
};

export const PAYMENT_STRATEGIES = {
  DIRECT: 'direct',
  FACTORED: 'factored',
  REVERSE_FACTORED: 'reverse_factored',
  PAYMENT_ON_SELL_THROUGH: 'payment_on_sell_through',
};

export const BADGE_KEYS = {
  ANY_CERTIFIED_SELLER: '-seller',
  ANY_LL_LIST: 'list-',
  CERTIFIED_SELLER_ELITE: 'elite-seller',
  CERTIFIED_SELLER_POWER: 'power-seller',
  COMPLIANT_SELLER: 'compliant',
  FINANCIAL: 'll-financial',
  LOGISTICS: 'll-logistics',
  NEW: 'new',
  PAYMENTS: 'on-payments',
  SOCIAL_EQUITY_VERIFIED: 'social-equity_verified',
  VERIFIED_SELLER: 'verified',
};

export const INVENTORY_TASKS_REQUIRED_ACTIONS = Object.freeze({
  LINK_CUSTOMER: 'link_customer',
  VERIFY_BUYER: 'verify_buyer',
});

const TIMEZONES = {
  CT: 'CT',
  ET: 'ET',
  HT: 'HT',
  MT: 'MT',
  PT: 'PT',
};

export const DEFAULT_STATES_TIMEZONES = Object.freeze({
  CA: TIMEZONES.PT,
  CO: TIMEZONES.MT,
  MA: TIMEZONES.ET,
  MI: TIMEZONES.CT,
  MO: TIMEZONES.CT,
  OR: TIMEZONES.PT,
});

export const CO_MAX_PICKUP_DATE_DAYS = 7;

/**
 * Session Storage
 */

export const SESSION_STORAGE_VALUES = {
  DISMISSED_CRM_CTA: 'crm__dismissed-payments-activation-CTA',
};

export const PAYMENT_TERMS_IDS = {
  COD: 20,
  NET_3: 8,
  NET_7: 2,
  NET_10: 5,
  NET_14: 9,
  NET_15: 3,
  NET_20: 6,
  NET_30: 4,
  NET_45: 22,
  NET_60: 7,
  NET_90: 14,
  PRE_PAYMENT: 10,
  CONSIGNMENT: 12,
};

export const PAYMENT_METHOD_IDS = {
  ACH: 4,
  CASH: 1,
  CHECK: 2,
  DIRECT: 6,
  FLEXIBLE: 5,
  WIRE: 3,
};

export const QUICKBOOKS_ERROR_CODES = {
  INACTIVE_CUSTOMER: 1,
  INACTIVE_PRODUCT: 2,
};

export const DISCOUNT_TYPES = {
  sale: {
    id: 'sale',
  },
  volume: {
    id: 'volume',
  },
  volumeV2: {
    id: 'volume_v2',
  },
};

export const STATES_WITH_L3_ROUTE_SCHEDULES = ['CO', 'IL', 'PA'];

export const STATES_WITH_SAME_DAY_PICKUP = ['IL', 'PA'];

/**
 * Acceptable values for the `changed_line_item.changed_property_name` field
 * used in the calculateLineItems method of services/api/order.ts
 */
export const CALCULATE_LINE_PROPERTY = {
  NEW_LINE_ITEM: 'new_line_item',
  DELETE_LINE_ITEM: 'delete_line_item',
  ORDERED_UNIT_PRICE: 'ordered_unit_price',
  QUANTITY: 'quantity',
  SALE_PRICE: 'sale_price',
  NOTES: 'notes',
  DISCOUNT: 'discount',
};

/**
 * After a company has a SaaS invoice that is overdue by the number of days, a cronjob runs to set their status to delinquent
 */
export const AUTOPAUSE_CUTTOFF_DAYS = 21;
export const AUTOPAUSE_CUTOFF_DAYS_V2 = 7;

/**
 * Invoices before this date are not considered for autopausing
 *
 * TODO(autopause): move date logic to api
 * https://leaflink.atlassian.net/browse/FIRE-5056
 */
export const AUTOPAUSE_CUTOFF_DATE = '2023-07-19';

export const SUBSCRIPTION_PLAN_FREE_TRIAL_PERIOD_MONTHS = 3;

export const LEAFLINK_PLUS_ID = '01tTQ000006MT4jYAG';
export const LEAFLINK_ACCESS_ID = '01tTQ000006MSYTYA4';

export const LEAFLINK_ACC_MANAGEMENT_EMAIL = 'account_management@leaflink.com';
